import { NotificationBadgeModel } from 'api-hooks/notification/model';
import { SalePurchaseTabEnum } from 'api-hooks/sale/common';
import { InvoicePaymentStatusEnum } from 'common/constants/enum';
import Separator from 'components/common/separator';
import Tabs from 'components/common/tabs';
import { NotificationBadge } from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';

interface Props {
  setActiveKey: (value: React.SetStateAction<React.Key>) => void;
  activeKey: React.Key;
  setStatus: (value: React.SetStateAction<string | undefined>) => void;
  badge?: { [key: string]: NotificationBadgeModel };
}

export function useSaleTabStatus() {
  const processStatus = [SalePurchaseTabEnum.Processed].join('|');
  const submittedStatus = [SalePurchaseTabEnum.Submitted].join('|');
  const needApprovalStatus = [SalePurchaseTabEnum.NeedApproval].join('|');
  const rejectedStatus = [SalePurchaseTabEnum.RejectedApproval].join('|');
  const completeStatus = [SalePurchaseTabEnum.Complete].join('|');
  const paidStatus = [InvoicePaymentStatusEnum.Finished].join('|');
  const canceledStatus = [SalePurchaseTabEnum.Canceled].join('|');
  const allStatus = [
    processStatus,
    submittedStatus,
    needApprovalStatus,
    rejectedStatus,
    completeStatus,
    canceledStatus,
  ].join('|');

  const tabStatus = {
    '0': allStatus,
    '1': processStatus,
    '2': submittedStatus,
    '3': needApprovalStatus,
    '4': rejectedStatus,
    '5': completeStatus,
    '6': paidStatus,
    '7': canceledStatus,
  } as const;

  return {
    processStatus,
    submittedStatus,
    needApprovalStatus,
    rejectedStatus,
    completeStatus,
    paidStatus,
    canceledStatus,
    allStatus,
    tabStatus,
  };
}

export default function SaleListTabs(props: Props) {
  const { t } = useTranslation();
  const { activeKey, setActiveKey, setStatus, badge } = props;

  const { tabStatus } = useSaleTabStatus();

  return (
    <Tabs
      activeKey={activeKey}
      onChangeActive={({ activeKey }) => {
        setActiveKey(activeKey);
        setStatus(tabStatus[activeKey]);
      }}
      items={[
        { title: t('common:all'), component: <></> },
        {
          title: (
            <>
              {t('common:processed')}
              {!!badge?.[SalePurchaseTabEnum.Processed] &&
                !!badge?.[SalePurchaseTabEnum.Processed].count && (
                  <>
                    <Separator gap={8} />
                    <NotificationBadge
                      style={{
                        backgroundColor:
                          badge?.[SalePurchaseTabEnum.Processed]
                            .background_color,
                        color: badge?.[SalePurchaseTabEnum.Processed].color,
                      }}
                    >
                      {badge?.[SalePurchaseTabEnum.Processed].count}
                    </NotificationBadge>
                  </>
                )}
            </>
          ),
          component: <></>,
        },
        {
          title: (
            <>
              {t('common:submitted')}
              {!!badge?.[SalePurchaseTabEnum.Submitted] &&
                !!badge?.[SalePurchaseTabEnum.Submitted].count && (
                  <>
                    <Separator gap={8} />
                    <NotificationBadge
                      style={{
                        backgroundColor:
                          badge?.[SalePurchaseTabEnum.Submitted]
                            .background_color,
                        color: badge?.[SalePurchaseTabEnum.Submitted].color,
                      }}
                    >
                      {badge?.[SalePurchaseTabEnum.Submitted].count}
                    </NotificationBadge>
                  </>
                )}
            </>
          ),
          component: <></>,
        },
        {
          title: (
            <>
              {t('common:need_approval')}
              {!!badge?.[SalePurchaseTabEnum.NeedApproval] &&
                !!badge?.[SalePurchaseTabEnum.NeedApproval].count && (
                  <>
                    <Separator gap={8} />
                    <NotificationBadge
                      style={{
                        backgroundColor:
                          badge?.[SalePurchaseTabEnum.NeedApproval]
                            .background_color,
                        color: badge?.[SalePurchaseTabEnum.NeedApproval].color,
                      }}
                    >
                      {badge?.[SalePurchaseTabEnum.NeedApproval].count}
                    </NotificationBadge>
                  </>
                )}
            </>
          ),
          component: <></>,
        },
        {
          title: (
            <>
              {t('common:rejected_approval')}
              {!!badge?.[SalePurchaseTabEnum.RejectedApproval] &&
                !!badge?.[SalePurchaseTabEnum.RejectedApproval].count && (
                  <>
                    <Separator gap={8} />
                    <NotificationBadge
                      style={{
                        backgroundColor:
                          badge?.[SalePurchaseTabEnum.RejectedApproval]
                            .background_color,
                        color:
                          badge?.[SalePurchaseTabEnum.RejectedApproval].color,
                      }}
                    >
                      {badge?.[SalePurchaseTabEnum.RejectedApproval].count}
                    </NotificationBadge>
                  </>
                )}
            </>
          ),
          component: <></>,
        },
        { title: t('common:complete'), component: <></> },
        { title: t('common:paid'), component: <></> },
        { title: t('common:canceled'), component: <></> },
      ]}
      overrideStyles={{
        tabList: { minHeight: '0px' },
        tab: { minHeight: '0px', height: '0', flexGrow: '0' },
        tabPanel: { minHeight: '0px', height: '0', flexGrow: '0' },
      }}
    />
  );
}
